/**
 * Saving a server-generated file (an export) from the browser.
 */

/** MIME types of the two export formats. */
export const EXPORT_MIME: Record<'csv' | 'xlsx', string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/**
 * Byte-order mark that makes Excel read a CSV as UTF-8 (CR-8703). Without it
 * Excel falls back to the machine's legacy code page, and the en dash in a
 * site name such as "Acme – Aerospace – UK" comes out as "â€“" (Windows) or
 * "‚Äì" (Mac). The backend's human-read CSV exports lead with it too.
 */
export const UTF8_BOM = '\uFEFF';

/**
 * A CSV built in the browser, as a Blob that opens correctly in Excel.
 *
 * Adds the UTF-8 BOM unless the text already starts with one, so a server
 * export that is re-read and re-saved never gets two.
 *
 * @example const blob = csvBlob([header, ...rows].join('\n'))
 * @param csv - the CSV text
 * @returns a `text/csv` Blob led by exactly one UTF-8 BOM
 */
export function csvBlob(csv: string): Blob {
  const text = csv.startsWith(UTF8_BOM) ? csv : UTF8_BOM + csv;
  return new Blob([text], { type: 'text/csv;charset=utf-8' });
}

/**
 * Hand a downloaded blob to the browser as a file.
 *
 * @example saveBlob(response.data, 'call-offs-2026-09-30.xlsx', EXPORT_MIME.xlsx)
 * @param data - the response body (`responseType: 'blob'`)
 * @param filename - the name the file is saved under
 * @param mime - its MIME type
 */
export function saveBlob(data: BlobPart, filename: string, mime: string): void {
  const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * The file name a response's Content-Disposition header offers.
 *
 * Prefers the RFC 5987 `filename*=UTF-8''…` form, which keeps non-ASCII
 * names intact, over the plain `filename="…"` fallback.
 *
 * @example dispositionFilename(res.headers['content-disposition']) ?? 'export.csv'
 * @param header - The Content-Disposition header value, if any
 * @returns The offered file name, or null when there is none
 */
export function dispositionFilename(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // Malformed percent-encoding — fall back to the plain name.
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  const name = plain?.[1].trim();
  return name || null;
}

/**
 * Pull a readable message out of a failed blob download.
 *
 * With `responseType: 'blob'` an axios error body is a Blob, so the usual
 * `err.message` yields only "Request failed with status code 400" and the
 * server's explanation is lost. Reads the blob back as JSON when present.
 *
 * @param err - The rejected axios error
 * @param fallback - Message to use when nothing better can be recovered
 * @returns A message safe to show the user
 */
export async function blobErrorMessage(err: unknown, fallback: string): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      const detail = parsed?.detail ?? parsed?.message;
      if (typeof detail === 'string' && detail) return detail;
    } catch {
      // Not JSON (an HTML error page, or an empty body) — fall through.
    }
  }
  return err instanceof Error ? err.message : fallback;
}
